// -----------------------------------------------------------------------------
// ActionPanel  (Phase 5.2 — full panel routing, #26I sidebar email)
//
// Slide-in panel that renders to the right of the guided checklist when the
// user taps an action CTA (UpNextCard primary button or a row click).
//
// Layout: the parent orchestrator (AttendedGuidedChecklist / V2) shrinks the
// checklist column to 43% and renders this panel at 57% beside it.
//
// Routing:
//   EMAIL_SENT / SEND_TREATMENT_PLAN / SEND_REVIEW_REQUEST
//       → calls onOpenSidebarTab('email') on mount + renders SidebarRedirectConfirmation
//         (known minor regression: linked_template_id is no longer auto-selected in
//          the sidebar composer; acceptable — user explicitly wants the sidebar UI)
//   All other known types → matched panel from PANEL_MAP (panels/index.js)
//   Unknown types → ComingSoonStub (defensive fallback only)
//
// Props:
//   item             — the checklist / UpNext item that was actioned
//   practiceId       — forwarded to panel
//   leadId           — forwarded to panel
//   leadDetail       — forwarded to panel
//   onClose          — called when user cancels
//   onSendSuccess    — unused for sidebar path; kept for API compatibility
//   onComplete       — called after panel success; parent marks item done + refresh
//   onOpenSidebarTab — called with 'email'|'whatsapp' to activate sidebar tab
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Button, useMediaQuery } from '@mui/material';
import { FocusTrap } from '@mui/base/FocusTrap';
import Iconify from 'src/components/Iconify';
import { PANEL_MAP } from './panels';
import SidebarRedirectConfirmation from './panels/SidebarRedirectConfirmation';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Action types that open the email composer
export const EMAIL_ACTION_TYPES = new Set([
    'EMAIL_SENT',
    'SEND_TREATMENT_PLAN',
    'SEND_REVIEW_REQUEST',
]);

// ---------------------------------------------------------------------------
// Stub shown for non-email action types
// ---------------------------------------------------------------------------
function ComingSoonStub({ item, onClose }) {
    const labelMap = {
        PHONE_CALLED:      'Log call',
        LOG_PHONE_CALL:    'Log call',
        WHATSAPP_SENT:     'Send WhatsApp',
        SMS_SENT:          'Send SMS',
        SEND_CONSENT_FORM: 'Send consent form',
        SEND_PAYMENT_LINK: 'Send payment link',
        SCHEDULE_REMINDER: 'Set reminder',
        ADD_NOTE:          'Add note',
        FREE_TEXT:         'Enter text',
    };
    const label = labelMap[item?.item_type] || item?.title || 'Take action';

    return (
        <Box
            data-testid="action-panel-stub"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 3,
                py: 4,
                gap: 2,
                textAlign: 'center',
            }}
        >
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '14px',
                    bgcolor: 'rgba(7,141,238,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon="mdi:tools" width={26} height={26} sx={{ color: '#078DEE' }} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    {label}
                </Typography>
                <Typography
                    data-testid="action-panel-stub-message"
                    sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5, letterSpacing: '-0.005em', px: 1 }}
                >
                    This action isn&apos;t wired up yet. Mark it complete from the checklist row when done.
                </Typography>
            </Box>
            <Button
                variant="outlined"
                size="small"
                onClick={onClose}
                sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
            >
                Close
            </Button>
        </Box>
    );
}
ComingSoonStub.propTypes = {
    item: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// ActionPanel
// ---------------------------------------------------------------------------

export default function ActionPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onClose,
    onComplete,
    handleReloadLeads,
    onOpenSidebarTab,
    noSidebar,
}) {
    const [mounted, setMounted] = useState(false);
    const panelRef = useRef(null);
    const isEmail = item && EMAIL_ACTION_TYPES.has(item.item_type);
    // Below 900px the panel collapses to full width (stacks vertically over the checklist).
    const isNarrow = useMediaQuery('(max-width: 900px)');

    // Trigger entrance animation after first paint.
    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 16);
        return () => clearTimeout(t);
    }, []);

    // Move focus to the first focusable element inside the panel after it appears.
    useEffect(() => {
        if (!mounted) return undefined;
        const t = setTimeout(() => {
            const focusable = panelRef.current?.querySelector(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            focusable?.focus();
        }, 50);
        return () => clearTimeout(t);
    }, [mounted]);

    // For email action types open the sidebar Email tab immediately on mount.
    // Fires once per panel open; item_type is stable during a panel's lifetime.
    useEffect(() => {
        if (isEmail && onOpenSidebarTab) {
            onOpenSidebarTab('email');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!item) return null;

    return (
        <FocusTrap open>
        <Box
            ref={panelRef}
            tabIndex={-1}
            data-testid="action-panel"
            data-item-type={item.item_type}
            role="dialog"
            aria-modal="true"
            aria-label={item.title || 'Action panel'}
            sx={{
                flex: isNarrow ? '0 0 100%' : '0 0 40%',
                minWidth: 0,
                bgcolor: '#ffffff',
                borderRadius: '16px',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                // Slide-in from right
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'none' : 'translateX(24px)',
                transition: `opacity 320ms ${EASING}, transform 320ms ${EASING}`,
            }}
        >
            {/* Panel header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.25,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1d1d1f',
                        letterSpacing: '-0.01em',
                    }}
                >
                    {item.title || item.name || 'Action'}
                </Typography>
                <IconButton
                    data-testid="action-panel-close"
                    size="small"
                    aria-label="Close panel"
                    onClick={onClose}
                    sx={{
                        p: 0.5,
                        color: '#6e6e73',
                        '&:hover': { color: '#1d1d1f', bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                >
                    <Iconify icon="mdi:close" width={16} height={16} />
                </IconButton>
            </Box>

            {/* Panel body — routed by item_type.
                 overflowX:hidden clips horizontal bleed (email composer widths);
                 vertical overflow is intentionally unrestricted so tall panels like
                 NextActionPanel can expand and show their Confirm button. */}
            <Box sx={{ flex: 1, minWidth: 0, overflowX: 'hidden', display: 'flex', flexDirection: 'column' }} data-testid="action-panel-body">
                {isEmail ? (
                    // Email types: sidebar tab was opened on mount (useEffect above).
                    // Show confirmation so user can mark item done after sending.
                    <SidebarRedirectConfirmation
                        channel="email"
                        body="Email composer opened in the sidebar — compose and send your email there, then mark this done."
                        doneLabel="Mark email sent"
                        onComplete={() => { if (onComplete) onComplete(item); }}
                        practiceId={practiceId}
                        leadId={leadId}
                        leadDetail={leadDetail}
                        noSidebar={noSidebar}
                    />
                ) : (() => {
                    // Route to the correct panel via PANEL_MAP (lazy-loaded components)
                    const PanelComponent = PANEL_MAP[item.item_type];
                    if (PanelComponent) {
                        return (
                            <PanelComponent
                                item={item}
                                practiceId={practiceId}
                                leadId={leadId}
                                leadDetail={leadDetail}
                                onClose={onClose}
                                onComplete={onComplete}
                                handleReloadLeads={handleReloadLeads}
                                onOpenSidebarTab={onOpenSidebarTab}
                                noSidebar={noSidebar}
                            />
                        );
                    }
                    // Defensive fallback for unknown action types
                    return <ComingSoonStub item={item} onClose={onClose} />;
                })()}
            </Box>
        </Box>
        </FocusTrap>
    );
}

ActionPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    /** Called after panel success; orchestrator marks item done + refresh. */
    onComplete: PropTypes.func,
    /** Deferred table reload — called ~3s after archive dispatched so Celery drains first. */
    handleReloadLeads: PropTypes.func,
    /** Called with 'email' or 'whatsapp' to switch the right-sidebar to the matching tab. */
    onOpenSidebarTab: PropTypes.func,
    /** Additive, default false. When true (sidebarless host like the full-screen
     *  Completed-Treatment hub), email/WhatsApp confirmation copy points to the
     *  lead's detail tabs instead of "the sidebar". */
    noSidebar: PropTypes.bool,
};

ActionPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
    handleReloadLeads: null,
    onOpenSidebarTab: null,
    noSidebar: false,
};

